import { Provider } from 'react-redux';
import { useKeystoneSession } from '../auth/use-keystone-session';
import { useEffect, useRef, type ReactNode } from 'react';

import { clearCurrentUser, setCurrentUser } from '../state-management/current-user';
import { expandCapabilities } from '../auth/role-capabilities';
import { syncAuth } from '../external-endpoints/sync-auth';
import { makeStore } from '../state-management/store';
import { AppRootContextProvider } from './app-root-context-provider';

export const AppGlobalProvider = ({ children }: { children?: ReactNode }) => {
	const { status, user, getToken } = useKeystoneSession();
	const storeRef = useRef<ReturnType<typeof makeStore>>(undefined);
	const syncedSubRef = useRef<string | null>(null);

	// The new-release watcher lives in ReleaseRefreshGuard, inside AppRootContextProvider, where the
	// designs it has to stash before the refresh are reachable.

	if (!storeRef.current) {
		storeRef.current = makeStore(getToken, {});
	}

	useEffect(() => {
		if (status !== 'ready' || !user) {
			storeRef.current?.dispatch(clearCurrentUser());
			syncedSubRef.current = null;
			return;
		}

		const { sub, displayName, orgId, appRoles } = user;
		const nameParts = (displayName ?? '').split(' ');
		const firstName = nameParts[0] ?? '';
		const lastName = nameParts.slice(1).join(' ');
		const initialAppRoles = appRoles ?? [];

		// JWT carries AppRole names only. Capabilities are expanded locally from the
		// static manifest mirror (apps/react-ui/src/auth/role-capabilities.ts).
		storeRef.current?.dispatch(setCurrentUser({
			id: sub,
			email: user.email ?? '',
			firstName,
			lastName,
			picture: null,
			appRoles: initialAppRoles,
			capabilities: expandCapabilities(initialAppRoles),
			orgId: orgId ?? null,
			active: true
		}));

		if (syncedSubRef.current !== sub) {
			syncedSubRef.current = sub;
			syncAuth({
				body: {
					sub,
					email: user.email ?? '',
					firstName,
					lastName,
					picture: ''
				},
				getToken
			}).then(([_syncedUser, syncError]) => {
				if (syncError) {
					console.warn('AppGlobalProvider: sync-auth failed', syncError);
				}
			});
		}
	}, [status, user]);

	return (
		<Provider store={storeRef.current}>
			<AppRootContextProvider>{children}</AppRootContextProvider>
		</Provider>
	);
};
